// Form Component to Add or Edit project task

import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  Space,
  DatePicker,
  Upload,
  Checkbox,
  TimePicker,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../../components/form/form";
import { FormRow } from "../../../../../components/form/form-row";
import {
  TDropdownOptions,
  TResponseModel,
  TProjectWork,
} from "../../../../../types/config";
import {
  eDocType,
  eMONTHMAPPING,
  ePriority,
  eResultCode,
  eRoleUniqueId,
  eSCHEDULETYPE,
  eWEEKDAYS,
} from "../../../../../utils/enum";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import {
  AddEditTask,
  AddEditWork,
  AddEditWorkCategory,
  GetSpecificProject,
  GetSpecificWork,
  DeleteDocs,
} from "../../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../../utils/input.validator";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CustomDropdown from "../../../../../components/custom-dropdown/custom-dropdown";
import { PageSize } from "../../../../../utils/constants";
import dayjs from "dayjs";
import JoditEditor from "jodit-react";
import { useRef } from "react";

type TTDrawerProps = {
  projectId: string;
  projectCategoryId: string;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function AddEditWorkForm(props: TTDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<TDropdownOptions[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast
  const [workCategories, setWorkCategories] = useState<TDropdownOptions[]>([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [scheduleType, setScheduleType] = useState(null);
  const [workDescription, setWorkDescription] = useState("");
  const editor = useRef(null);
  const [monthDays, setMonthDays] = useState<TDropdownOptions[]>([
    { label: "Start of Month", value: eMONTHMAPPING.STARTOFMONTH },
    { label: "Mid of Month", value: eMONTHMAPPING.MIDOFMONTH },
    { label: "End of Month", value: eMONTHMAPPING.ENDOFMONTH },
  ]);
  const [initialDocuments, setInitialDocuments] = useState<string[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);

  // default values initial state
  const defaultValues: TProjectWork = {
    id: props.id || 0,
    projectId: props.projectId || "",
    projectCategoryId: parseInt(props.projectCategoryId) || 0,
    workCategoryId: null,
    managerId: "",
    workTitle: "",
    workDescription: "",
    startDate: "",
    endDate: "",
    // isRecurring: false,
    estimatedTime: 0,
    priority: null,
  };

  const priorityOptions = [
    {
      label: "Low",
      value: ePriority.LOW,
    },
    {
      label: "Medium",
      value: ePriority.MEDIUM,
    },
    {
      label: "High",
      value: ePriority.HIGH,
    },
  ];

  const editorConfig = {
    readonly: false,
    height: 150,
    toolbarAdaptive: false,
    toolbarSticky: true,
    buttons: "bold,italic,underline,ul,ol,font,fontsize,align,link",
    style: {
      fontFamily: "inherit",
      fontSize: "14px",
    },
  };

  useEffect(() => {
    if (scheduleType === eSCHEDULETYPE.DAILY) {
      form.setFieldsValue({ day: undefined, reportDate: undefined });
    } else if (scheduleType === eSCHEDULETYPE.WEEKLY) {
      form.setFieldsValue({ reportDate: undefined });
    } else if (scheduleType === eSCHEDULETYPE.MONTHLY) {
      form.setFieldsValue({ day: undefined });
    }
  }, [scheduleType, form]);

  const deleteDocuments = async (docUrls: string[]) => {
    if (docUrls.length === 0) return;

    try {
      const response = await post(DeleteDocs, {
        data: {
          docUrls: docUrls,
        },
      });

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // Documents deleted successfully
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error deleting documents:", error);
    }
  };

  // Handle on submit event
  const onFinish = async (values: TProjectWork) => {
    try {
      const { attachments, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          workDescription: workDescription, // Use the rich text content
          projectId: props.projectId,
          startDateTime: dayjs(values.startDate),
          endDateTime: dayjs(values.endDate),
          id: props.id ? props.id : 0,
          projectCategoryId: props.projectCategoryId,
          // isRecurring: values.isRecurring ? 1 : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditWork, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // Delete documents marked for deletion
        if (documentsToDelete.length > 0) {
          await deleteDocuments(documentsToDelete);
        }

        try {
          // Filter only new files (ones with originFileObj)
          const newFiles =
            values.attachments?.filter((file: any) => file.originFileObj) || [];

          if (newFiles.length > 0) {
            const formData = new FormData();

            newFiles.forEach((file: any) => {
              formData.append("files", file.originFileObj);
            });
            formData.append("projectId", response.data?.projectId);
            formData.append("projectWorkMappingId", response.data?.id);
            formData.append("docType", eDocType.WORK_MEDIA.toString());

            const uploadResponse = await post("/uploadMedia", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            if (
              uploadResponse.dataResponse.returnCode === eResultCode.SUCCESS
            ) {
            } else {
              onShowToast({
                type: ToastType.error,
                title: <CloseOutlined />,
                position: ToastOpen.centerTop,
                content: uploadResponse.dataResponse.description,
              });
            }
          }
        } catch (error) {
          console.error("Error:", error);
        }
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Something went wrong!",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecifWork = async (id: number) => {
    try {
      const response = await post(GetSpecificWork, {
        data: {
          id: id,
        },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        form.setFieldsValue({
          workCategoryId: data.workCategoryId,
          managerId: data.managerId,
          workTitle: data.workTitle,
          workDescription: data.workDescription,
          startDate: data?.startDate ? dayjs(data.startDate) : "",
          endDate: data?.endDate ? dayjs(data.endDate) : "",
          estimatedTime: data.estimatedTime,
          priority: data.priority,
          // isRecurring: data.isRecurring,
        });
        setWorkDescription(data.workDescription || "");
        // setIsRecurring(data?.isRecurring === 1 ? true : false);

        // Bind documents to upload field
        if (data.documents && data.documents.length > 0) {
          const docUrls = data.documents.map((doc: any) => doc.docURL);
          setInitialDocuments(docUrls);

          const uploadFiles = data.documents.map((doc: any, index: number) => ({
            uid: `-${index + 1}`,
            name: doc.docName,
            status: "done",
            url: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
            thumbUrl: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
          }));
          form.setFieldsValue({ attachments: uploadFiles });
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching project options:", error);
    }
  };

  const fetchWorkCategories = async (projectId: string) => {
    try {
      const response = await post(GetSpecificProject, {
        data: {
          id: projectId,
        },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const workCategories = data?.projectcategory?.workCategory.map(
          (item: any) => ({
            label: item.name,
            value: item.id,
          })
        );
        setWorkCategories(workCategories);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching project options:", error);
    }
  };

  useEffect(() => {
    if (props.projectId) {
      fetchWorkCategories(props.projectId);
    }
    if (props.id) {
      fetchSpecifWork(props.id);
    }
  }, []);

  return (
    <FormLayout>
      <FormLayout>
        <Form
          form={form}
          name='register'
          onFinish={onFinish}
          initialValues={defaultValues}
          layout='vertical'
          // onValuesChange={(changedValues) => {
          //   if (changedValues.isRecurring !== undefined) {
          //     setIsRecurring(changedValues.isRecurring);
          //   }
          //   if (changedValues.isRecurring) {
          //     form.setFieldValue("endDate", "");
          //   }
          //   if (changedValues.scheduleType) {
          //     setScheduleType(changedValues.scheduleType);
          //   }
          // }}
        >
          <FormHeader>
            <CloseOutlined
              style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
              onClick={() => {
                props.onCloseDrawer();
              }}
            />
          </FormHeader>

          <FormContent>
            {/* <Row gutter={16}>
              <Space>
                <Col span={24}>
                  <h2 style={{ color: "#fff", marginTop: "10px" }}>
                    {`${props.id ? "Edit" : "Add"} a Project Work`}
                  </h2>
                </Col>
              </Space>
            </Row> */}
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={12}>
                <Form.Item
                  name='workCategoryId'
                  label='Work Category'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("select Work Category"),
                    },
                  ]}
                >
                  <Select
                    placeholder='Select Work Category'
                    style={{ width: "100%" }}
                  >
                    {workCategories?.map((workCategory: TDropdownOptions) => (
                      <Select.Option
                        key={workCategory.value}
                        value={workCategory.value}
                      >
                        {workCategory.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='managerId'
                  label='Manager Name'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("select Manager Name"),
                    },
                  ]}
                >
                  <CustomDropdown
                    endPoint={"/GetUserList"}
                    value={form.getFieldValue("managerId")}
                    // addPaylod={{ roleUniqueId: eRoleUniqueId.MANAGER }}
                    optionName='fullName'
                    selectedName={form.getFieldValue("managerName")}
                    onChange={(selected: any) => {
                      form.setFieldValue("managerId", selected.value);
                      form.setFieldValue("managerName", selected.label);
                    }}
                    label=''
                    placeHolder='Select Manager Name'
                  ></CustomDropdown>
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={24}>
                <Form.Item
                  name='workTitle'
                  label='Work Name'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Enter work name"),
                    },
                  ]}
                >
                  <Input placeholder='Enter work name' />
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={12}>
                <Form.Item
                  name='priority'
                  label='Select Priority'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("select priority"),
                    },
                  ]}
                >
                  <Select
                    placeholder='Select Priority'
                    style={{ width: "100%" }}
                  >
                    {priorityOptions.map((priority: TDropdownOptions) => (
                      <Select.Option
                        key={priority.value}
                        value={priority.value}
                      >
                        {priority.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='estimatedTime'
                  label='Estimated Time(In Minutes)'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("select estimatedTime"),
                    },
                  ]}
                >
                  <Input placeholder='Enter Estimated Time' />
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={12}>
                <Form.Item
                  name='startDate'
                  label='Start Date'
                  required
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder='Select Start Date'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='endDate'
                  label='End Date'
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(
                            new Error("End Date is required")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder='Select End Date'
                    // disabled={isRecurring}
                  />
                </Form.Item>
                {/* <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Form.Item
                    name='isRecurring'
                    valuePropName='checked'
                    style={{ position: "relative", bottom: "28px" }}
                  >
                    <Checkbox
                      onChange={(e) => {
                        form.setFieldsValue({ isRecurring: e.target.checked });
                      }}
                    >
                      Recurring Work
                    </Checkbox>
                  </Form.Item>
                </div> */}
              </Col>
            </Row>
            {/* {isRecurring && (
              <>
                <Row
                  gutter={16}
                  className='formRowHeight'
                >
                  <Col span={24}>
                    <Form.Item
                      name='workScheduleType'
                      valuePropName='workScheduleType'
                      label='Schedule Type'
                      rules={[
                        {
                          required: true,
                          message: "Please select schedule type",
                        },
                      ]}
                    >
                      <Select
                        placeholder='Select schedule type'
                        options={[
                          { label: "Daily", value: eSCHEDULETYPE.DAILY },
                          { label: "Weekly", value: eSCHEDULETYPE.WEEKLY },
                          { label: "Monthly", value: eSCHEDULETYPE.MONTHLY },
                        ]}
                        onChange={(value: any) => setScheduleType(value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {(scheduleType === eSCHEDULETYPE.WEEKLY ||
                  scheduleType === eSCHEDULETYPE.MONTHLY) && (
                  <>
                    <Row
                      gutter={16}
                      className='formRowHeight'
                    >
                      <Col span={24}>
                        <Form.Item
                          name='day'
                          label='Day'
                          rules={[
                            { required: true, message: "Please select day" },
                          ]}
                        >
                          <Select
                            mode='multiple'
                            placeholder='Select day(s)'
                            options={
                              scheduleType === eSCHEDULETYPE.WEEKLY
                                ? [
                                    {
                                      label: "Sunday",
                                      value: eWEEKDAYS.SUNDAY,
                                    },
                                    {
                                      label: "Monday",
                                      value: eWEEKDAYS.MONDAY,
                                    },
                                    {
                                      label: "Tuesday",
                                      value: eWEEKDAYS.TUESDAY,
                                    },
                                    {
                                      label: "Wednesday",
                                      value: eWEEKDAYS.WEDNESDAY,
                                    },
                                    {
                                      label: "Thursday",
                                      value: eWEEKDAYS.THURSDAY,
                                    },
                                    {
                                      label: "Friday",
                                      value: eWEEKDAYS.FRIDAY,
                                    },
                                    {
                                      label: "Saturday",
                                      value: eWEEKDAYS.SATURDAY,
                                    },
                                  ]
                                : monthDays
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                <Row
                  gutter={16}
                  className='formRowHeight'
                >
                  <Col span={24}>
                    <Form.Item
                      name='scheduleTime'
                      label='Start Time'
                      rules={[
                        { required: true, message: "Please select time" },
                      ]}
                    >
                      <TimePicker
                        format='HH:mm:ss'
                        placeholder='Select start time'
                        use12Hours={false}
                        style={{ width: "100%" }}
                        onChange={(time, timeString) => {
                          form.setFieldValue("scheduleTime", time);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )} */}

            <Row
              gutter={16}
              className='formRowHeight'
              style={{
                marginBottom: "130px",
              }}
            >
              <Col span={24}>
                <Form.Item
                  name='workDescription'
                  label='Work Description'
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        if (
                          !workDescription ||
                          workDescription.trim() === "" ||
                          workDescription === "<p><br></p>"
                        ) {
                          return Promise.reject(
                            new Error("Please enter work description")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <div
                    style={{ border: "1px solid #d9d9d9", borderRadius: "6px" }}
                  >
                    <JoditEditor
                      ref={editor}
                      value={workDescription}
                      config={editorConfig}
                      onBlur={(newContent) => {
                        setWorkDescription(newContent);
                        form.setFieldValue("workDescription", newContent);
                      }}
                      onChange={(newContent) => {}}
                    />
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={16}
              className='formRowHeight'
              style={{ marginTop: "20px" }}
            >
              <Col span={24}>
                <Form.Item
                  name='attachments'
                  label='Attachments'
                  valuePropName='fileList'
                  getValueFromEvent={(e) =>
                    Array.isArray(e) ? e : e?.fileList
                  }
                >
                  <Upload
                    name='files'
                    listType='picture-card'
                    multiple={true}
                    beforeUpload={() => false}
                    onChange={({ fileList }) => {
                      form.setFieldValue("attachments", fileList);
                    }}
                    onRemove={(file) => {
                      if (file.url) {
                        // Extract the doc_url part from the full URL
                        const urlParts = file.url.split("//");
                        const docUrl =
                          urlParts.length > 1
                            ? urlParts[urlParts.length - 1]
                            : file.url.replace(
                                `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/`,
                                ""
                              );

                        if (initialDocuments.includes(docUrl)) {
                          setDocumentsToDelete((prev) => [...prev, docUrl]);
                        }
                      }
                      return true;
                    }}
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </FormContent>
          <FormFooter className='formFooter'>
            <Button
              type='default'
              disabled={isLoading}
              className='btnDimensions'
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              className='btnDimensions'
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
          </FormFooter>
        </Form>
      </FormLayout>
    </FormLayout>
  );
}
